import React from 'react';
import {Platform, Pressable, StyleSheet, Text, View} from 'react-native';
import {initialWindowMetrics} from 'react-native-safe-area-context';
import Svg, {Circle, Path} from 'react-native-svg';
import {colors, fonts, radii, shadows, spacing} from '../config/theme';

// Toast styled to the app's design tokens — a white card with a tinted
// status icon, replacing react-native-toast-message's default look.

const VARIANTS = {
  success: {
    tint: colors.mintSoft,
    color: colors.secondary,
    icon: 'M7.5 12.5l3 3 6-6.5',
  },
  error: {
    tint: colors.errorContainer,
    color: colors.error,
    icon: 'M12 7.5v5.5M12 16.5v.01',
  },
  info: {
    tint: colors.mintWash,
    color: colors.primary,
    icon: 'M12 11v5.5M12 7.5v.01',
  },
};

const ToastCard = ({variant, text1, text2, onPress, hide}) => {
  const {tint, color, icon} = VARIANTS[variant];
  return (
    <Pressable
      onPress={onPress || hide}
      accessibilityRole="alert"
      style={styles.card}>
      <View style={[styles.iconWrap, {backgroundColor: tint}]}>
        <Svg width={20} height={20} viewBox="0 0 24 24" fill="none">
          <Circle cx={12} cy={12} r={9.5} stroke={color} strokeWidth={1.8} />
          <Path
            d={icon}
            stroke={color}
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </Svg>
      </View>
      <View style={styles.texts}>
        {text1 ? (
          <Text style={styles.title} numberOfLines={2}>
            {text1}
          </Text>
        ) : null}
        {text2 ? (
          <Text style={styles.message} numberOfLines={3}>
            {text2}
          </Text>
        ) : null}
      </View>
    </Pressable>
  );
};

export const toastConfig = {
  success: props => <ToastCard variant="success" {...props} />,
  error: props => <ToastCard variant="error" {...props} />,
  info: props => <ToastCard variant="info" {...props} />,
};

// Clear the status bar / Dynamic Island instead of the library's fixed 40.
export const toastTopOffset = (initialWindowMetrics?.insets.top ?? 40) + 8;

const styles = StyleSheet.create({
  card: {
    width: '92%',
    // Wide browser windows: a compact toast instead of a full-width bar.
    ...Platform.select({web: {maxWidth: 440}}),
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
    paddingVertical: spacing.md,
    paddingHorizontal: spacing.md,
    borderRadius: radii.card,
    backgroundColor: colors.surface,
    borderWidth: 1,
    borderColor: colors.border,
    ...shadows.level2,
  },
  iconWrap: {
    width: 40,
    height: 40,
    borderRadius: 20,
    alignItems: 'center',
    justifyContent: 'center',
  },
  texts: {
    flex: 1,
    gap: 2,
  },
  title: {
    fontFamily: fonts.semiBold,
    fontSize: 14,
    lineHeight: 20,
    color: colors.text,
  },
  message: {
    fontFamily: fonts.regular,
    fontSize: 13,
    lineHeight: 18,
    color: colors.textMuted,
  },
});
